import React, { useState, useEffect, useCallback } from 'react';
import {
    StorageSource,
    UnifiedFile,
    getStorageSources,
    browseUnified,
    uploadToSource,
    createFolderInSource,
    deleteFromSource,
    moveFile,
    copyFile,
    syncFromFTP
} from '../../../../services/mediaApi';
import ImageEditor from './ImageEditor';
import AIImageGenerator from './AIImageGenerator';
import FileViewer from './FileViewer';
import StockPhotosBrowser from './StockPhotosBrowser';

interface UnifiedFileManagerProps {
    theme?: 'light' | 'dark';
    language?: 'en' | 'sv';
    onSelect?: (file: UnifiedFile) => void;
    onSelectMultiple?: (files: UnifiedFile[]) => void;
    multiSelect?: boolean;
    initialSource?: string;
    initialPath?: string;
}

const translations = {
    en: {
        title: 'File Manager',
        subtitle: 'Unified Storage Browser',
        sources: 'Storage Sources',
        local: 'Local Storage',
        ftp: 'FTP Storage',
        cloud: 'Cloud Storage',
        upload: 'Upload',
        newFolder: 'New Folder',
        delete: 'Delete',
        move: 'Move',
        copy: 'Copy',
        sync: 'Sync from FTP',
        search: 'Search files...',
        noFiles: 'No files found',
        loading: 'Loading...',
        connected: 'Connected',
        disconnected: 'Disconnected',
        refresh: 'Refresh',
        back: 'Back',
        home: 'Home',
        selectFile: 'Select File',
        confirmDelete: 'Are you sure you want to delete this item?',
        syncing: 'Syncing...',
        syncComplete: 'Sync complete',
        uploadComplete: 'Upload complete',
        folderCreated: 'Folder created',
        deleted: 'Deleted successfully',
        error: 'An error occurred'
    },
    sv: {
        title: 'Filhanterare',
        subtitle: 'Unified Storage Browser',
        sources: 'Lagringskällor',
        local: 'Lokal Lagring',
        ftp: 'FTP Lagring',
        cloud: 'Molnlagring',
        upload: 'Ladda upp',
        newFolder: 'Ny Mapp',
        delete: 'Radera',
        move: 'Flytta',
        copy: 'Kopiera',
        sync: 'Synka från FTP',
        search: 'Sök filer...',
        noFiles: 'Inga filer hittades',
        loading: 'Laddar...',
        connected: 'Ansluten',
        disconnected: 'Frånkopplad',
        refresh: 'Uppdatera',
        back: 'Tillbaka',
        home: 'Hem',
        selectFile: 'Välj Fil',
        confirmDelete: 'Är du säker på att du vill radera detta?',
        syncing: 'Synkar...',
        syncComplete: 'Synkning klar',
        uploadComplete: 'Uppladdning klar',
        folderCreated: 'Mapp skapad',
        deleted: 'Raderad',
        error: 'Ett fel uppstod'
    }
};

const UnifiedFileManager: React.FC<UnifiedFileManagerProps> = ({
    theme = 'dark',
    language = 'en',
    onSelect,
    onSelectMultiple,
    multiSelect = false,
    initialSource = 'local',
    initialPath = ''
}) => {
    const isDark = theme === 'dark';
    const t = translations[language] || translations.en;

    // State
    const [sources, setSources] = useState<StorageSource[]>([]);
    const [activeSource, setActiveSource] = useState(initialSource);
    const [currentPath, setCurrentPath] = useState(initialPath);
    const [pathHistory, setPathHistory] = useState<string[]>([]);
    const [files, setFiles] = useState<UnifiedFile[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<UnifiedFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; file: UnifiedFile | null }>({ x: 0, y: 0, file: null });
    const [showSidebar, setShowSidebar] = useState(true);
    const [showImageEditor, setShowImageEditor] = useState(false);
    const [showAIGenerator, setShowAIGenerator] = useState(false);
    const [showFileViewer, setShowFileViewer] = useState(false);
    const [viewingFile, setViewingFile] = useState<UnifiedFile | null>(null);
    const [editingFile, setEditingFile] = useState<UnifiedFile | null>(null);
    const [showStockPhotos, setShowStockPhotos] = useState(false);

    // Helper for cloud connection status
    const currentSourceObj = sources.find(s => s.id === activeSource);
    const isCloudSource = activeSource?.startsWith('cloud-');
    const isDisconnected = isCloudSource && currentSourceObj && !currentSourceObj.connected;

    // Handle right click context menu
    const handleContextMenu = (e: React.MouseEvent, file: UnifiedFile) => {
        e.preventDefault();
        setContextMenu({
            x: e.clientX,
            y: e.clientY,
            file
        });
    };

    // Close context menu on click elsewhere
    useEffect(() => {
        const handleClick = () => setContextMenu({ x: 0, y: 0, file: null });
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, []);

    // Rename (Move within same folder)
    const handleRename = async (file: UnifiedFile) => {
        const newName = prompt(language === 'sv' ? 'Nytt namn:' : 'New name:', file.name);
        if (!newName || newName === file.name) return;

        // Construct current folder path correctly. If currentPath is empty, file is at root.
        const folderPath = currentPath;
        const fromPath = file.path; // API usually needs relative path from source root

        // Basic path construction - assuming file.path is correct for 'from'
        // For 'to', we need to replace the filename part of the path
        const pathParts = file.path.split('/');
        pathParts.pop(); // remove old filename
        const toPath = [...pathParts, newName].join('/');

        const result = await moveFile({
            source: activeSource,
            fromPath: fromPath,
            toPath: toPath
        });

        if (result.success) {
            showMessage('success', 'Renamed successfully');
            browse();
        } else {
            showMessage('error', result.error || t.error);
        }
    };

    // Download file
    const handleDownload = (file: UnifiedFile) => {
        if (!file.url) {
            showMessage('error', 'No download URL available');
            return;
        }
        const link = document.createElement('a');
        link.href = file.url;
        link.download = file.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showMessage('success', language === 'sv' ? 'Nedladdning startad' : 'Download started');
    };

    // Share file (copy link)
    const handleShare = (file: UnifiedFile) => {
        if (!file.url) {
            showMessage('error', 'No shareable URL available');
            return;
        }
        const fullUrl = window.location.origin + file.url;
        navigator.clipboard.writeText(fullUrl);
        showMessage('success', language === 'sv' ? 'Länk kopierad!' : 'Link copied to clipboard!');
    };

    // Open Image Editor
    const handleEditImage = (file: UnifiedFile) => {
        if (!file.url || file.isDirectory) return;
        setEditingFile(file);
        setShowImageEditor(true);
    };

    // Handle edited image save
    const handleSaveEditedImage = async (blob: Blob, newName: string) => {
        try {
            const file = new File([blob], newName, { type: 'image/jpeg' });
            await uploadToSource(file, { destination: activeSource, folder: currentPath || 'general' });
            showMessage('success', language === 'sv' ? 'Bild sparad!' : 'Image saved!');
            setShowImageEditor(false);
            setEditingFile(null);
            browse();
        } catch (error) {
            showMessage('error', t.error);
        }
    };

    // Handle AI generated image (backend already saves to /uploads/ai-generated/)
    const handleAIImageGenerated = async (imageUrl: string, prompt: string) => {
        try {
            showMessage('success', language === 'sv' ? 'AI-bild genererad!' : 'AI image generated!');
            setShowAIGenerator(false);
            browse(); // Refresh file list
        } catch (error) {
            showMessage('error', t.error);
        }
    };

    // Handle File Preview/View
    const handlePreview = (file: UnifiedFile) => {
        if (file.isDirectory) {
            navigateToFolder(file);
        } else {
            setViewingFile(file);
            setShowFileViewer(true);
        }
    };

    // Handle Text File Save
    const handleSaveTextFile = async (content: string) => {
        if (!viewingFile) return;
        try {
            const blob = new Blob([content], { type: 'text/plain' });
            const file = new File([blob], viewingFile.name, { type: 'text/plain' });
            await uploadToSource(file, { destination: activeSource, folder: currentPath || 'general' });
            showMessage('success', language === 'sv' ? 'Fil sparad!' : 'File saved!');
            // Optional: refresh or keep viewer open
        } catch (error) {
            showMessage('error', t.error);
        }
    };

    // Show message
    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3000);
    };

    // Load storage sources
    useEffect(() => {
        let mounted = true;
        const loadSources = async () => {
            try {
                const result = await getStorageSources();
                if (mounted && result.success) {
                    setSources(result.sources);
                }
            } catch (e) {
                console.error("Failed to load storage sources", e);
            }
        };
        loadSources();
        return () => { mounted = false; };
    }, []);

    // Browse files
    const browse = useCallback(async () => {
        setLoading(true);
        try {
            const result = await browseUnified({
                source: activeSource,
                path: currentPath,
                search: searchQuery
            });
            if (result.success) {
                setFiles(result.items);
            } else {
                setFiles([]); // Clear files on error to avoid stale state
                console.error('Browse failed:', result);
            }
        } catch (error) {
            console.error('Browse error:', error);
            setFiles([]); // Prevent undefined
            showMessage('error', t.error);
        } finally {
            setLoading(false);
        }
    }, [activeSource, currentPath, searchQuery, t.error]);

    useEffect(() => {
        browse();
    }, [browse]);

    // Navigate to folder
    const navigateToFolder = (folder: UnifiedFile) => {
        if (folder.isDirectory) {
            setPathHistory([...pathHistory, currentPath]);
            setCurrentPath(folder.path);
            setSelectedFiles([]);
        }
    };

    // Go back
    const goBack = () => {
        if (pathHistory.length > 0) {
            const newHistory = [...pathHistory];
            const previousPath = newHistory.pop() || '';
            setPathHistory(newHistory);
            setCurrentPath(previousPath);
        }
    };

    // Go to root
    const goHome = () => {
        setPathHistory([]);
        setCurrentPath('');
    };

    // Handle file click
    const handleFileClick = (file: UnifiedFile) => {
        if (file.isDirectory) {
            navigateToFolder(file);
        } else {
            if (multiSelect) {
                const isSelected = selectedFiles.some(f => f.path === file.path);
                const newSelected = isSelected
                    ? selectedFiles.filter(f => f.path !== file.path)
                    : [...selectedFiles, file];
                setSelectedFiles(newSelected);
                onSelectMultiple?.(newSelected);
            } else {
                setSelectedFiles([file]);
                onSelect?.(file);
            }
        }
    };

    // Handle upload
    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0) return;

        setLoading(true);
        for (const file of Array.from(fileList)) {
            const result = await uploadToSource(file, {
                destination: activeSource,
                folder: currentPath || 'general'
            });
            if (!result.success) {
                showMessage('error', result.error || t.error);
                break;
            }
        }
        showMessage('success', t.uploadComplete);
        browse();
        e.target.value = '';
    };

    // Create folder
    const handleCreateFolder = async () => {
        const name = prompt(language === 'sv' ? 'Namn på ny mapp:' : 'New folder name:');
        if (!name) return;

        const result = await createFolderInSource(name, {
            source: activeSource,
            path: currentPath
        });

        if (result.success) {
            showMessage('success', t.folderCreated);
            browse();
        } else {
            showMessage('error', result.error || t.error);
        }
    };

    // Delete
    const handleDelete = async (file: UnifiedFile) => {
        if (!confirm(t.confirmDelete)) return;

        const result = await deleteFromSource({
            source: activeSource,
            path: file.path
        });

        if (result.success) {
            showMessage('success', t.deleted);
            browse();
        } else {
            showMessage('error', result.error || t.error);
        }
    };

    // Sync from FTP
    const handleSync = async () => {
        setSyncing(true);
        const result = await syncFromFTP(currentPath || 'uploads');
        setSyncing(false);

        if (result.success) {
            showMessage('success', `${t.syncComplete}: ${result.synced?.length || 0} files`);
            browse();
        } else {
            showMessage('error', result.message || t.error);
        }
    };

    // Format file size
    const formatSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    // Get icon for file
    const getFileIcon = (file: UnifiedFile) => {
        if (file.isDirectory) return 'folder';
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')) return 'image';
        if (['mp4', 'webm', 'mov'].includes(ext || '')) return 'movie';
        if (['pdf'].includes(ext || '')) return 'picture_as_pdf';
        if (['doc', 'docx'].includes(ext || '')) return 'description';
        return 'insert_drive_file';
    };

    // Check if file is image
    const isImage = (file: UnifiedFile) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '');
    };

    return (
        <div className={`h-full flex flex-col ${isDark ? 'bg-slate-900 text-white' : 'bg-gray-50 text-slate-900'}`}>
            {/* Header */}
            <div className={`flex-shrink-0 px-6 py-4 border-b ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-3">
                            <span className="material-icons text-indigo-500">folder_open</span>
                            {t.title}
                        </h1>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        {/* Mobile sidebar toggle */}
                        <button
                            onClick={() => setShowSidebar(!showSidebar)}
                            className={`md:hidden p-2.5 rounded-xl ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                        >
                            <span className="material-icons">{showSidebar ? 'menu_open' : 'menu'}</span>
                        </button>

                        <label className="flex items-center gap-2 px-3 sm:px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
                            <span className="material-icons text-lg">cloud_upload</span>
                            <span className="hidden sm:inline">{t.upload}</span>
                            <input
                                type="file"
                                multiple
                                className="hidden"
                                onChange={handleUpload}
                            />
                        </label>
                        <button
                            onClick={handleCreateFolder}
                            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl font-medium ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                        >
                            <span className="material-icons text-lg">create_new_folder</span>
                            <span className="hidden sm:inline">{t.newFolder}</span>
                        </button>
                        <button
                            onClick={() => setShowAIGenerator(true)}
                            className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                        >
                            <span className="material-icons text-lg">auto_awesome</span>
                            <span className="hidden sm:inline">{language === 'sv' ? 'AI Generera' : 'AI Generate'}</span>
                        </button>
                        {activeSource === 'ftp' && (
                            <button
                                onClick={handleSync}
                                disabled={syncing}
                                className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50"
                            >
                                <span className={`material-icons text-lg ${syncing ? 'animate-spin' : ''}`}>
                                    {syncing ? 'sync' : 'cloud_sync'}
                                </span>
                                <span className="hidden sm:inline">{syncing ? t.syncing : t.sync}</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Message */}
                {message && (
                    <div className={`mt-3 px-4 py-2 rounded-lg ${message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {message.text}
                    </div>
                )}
            </div>

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Mobile Source Bar - visible only on mobile */}
                <div className={`md:hidden flex-shrink-0 px-3 py-2 border-b overflow-x-auto ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-white'}`}>
                    <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                            {t.sources}:
                        </span>
                        {sources.map(source => (
                            <button
                                key={`mobile-${source.id}`}
                                onClick={() => {
                                    setActiveSource(source.id);
                                    setCurrentPath('');
                                    setPathHistory([]);
                                    setShowStockPhotos(false);
                                    setShowSidebar(false);
                                }}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${activeSource === source.id && !showStockPhotos
                                    ? 'bg-indigo-600 text-white'
                                    : isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                title={source.description}
                            >
                                <span className="material-icons text-sm">{source.icon}</span>
                                {source.name}
                            </button>
                        ))}
                        <button
                            onClick={() => {
                                setShowStockPhotos(true);
                                setActiveSource('');
                                setShowSidebar(false);
                            }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${showStockPhotos
                                ? 'bg-purple-600 text-white'
                                : isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            <span className="material-icons text-sm">collections</span>
                            Stock
                        </button>
                    </div>
                </div>

                <div className="flex-1 flex overflow-hidden">
                    {/* Sidebar - Sources (desktop only) */}
                    <div className={`hidden md:block md:w-64 flex-shrink-0 border-r overflow-y-auto p-4 transition-all ${isDark ? 'border-slate-700 bg-slate-800/30' : 'border-gray-200 bg-white'}`}>
                        <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                            <span className="material-icons text-sm align-middle mr-1">dns</span>
                            {t.sources}
                        </h3>
                        <div className="space-y-2">
                            {sources.map(source => (
                                <button
                                    key={source.id}
                                    onClick={() => {
                                        setActiveSource(source.id);
                                        setCurrentPath('');
                                        setPathHistory([]);
                                        setShowStockPhotos(false);
                                    }}
                                    className={`w-full text-left p-3 rounded-xl transition-all flex items-center gap-3 ${activeSource === source.id && !showStockPhotos
                                        ? 'bg-indigo-600 text-white'
                                        : isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-50 hover:bg-gray-100'
                                        }`}
                                >
                                    <span className={`material-icons ${activeSource === source.id ? 'text-white' : 'text-indigo-400'}`}>
                                        {source.icon}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm truncate">{source.name}</div>
                                        <div className={`text-xs truncate ${activeSource === source.id ? 'text-indigo-200' : isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                            {source.connected !== undefined && (
                                                <span className={source.connected ? 'text-green-400' : 'text-red-400'}>
                                                    {source.connected ? '●' : '○'}
                                                </span>
                                            )}
                                            {' '}{source.description}
                                        </div>
                                    </div>
                                </button>
                            ))}

                            {/* Stock Photos Section */}
                            <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                                <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                    <span className="material-icons text-sm align-middle mr-1">photo_library</span>
                                    Stock Photos
                                </h4>
                                <button
                                    onClick={() => {
                                        setShowStockPhotos(true);
                                        setActiveSource('');
                                    }}
                                    className={`w-full text-left p-3 rounded-xl transition-all flex items-center gap-3 ${showStockPhotos
                                        ? 'bg-purple-600 text-white'
                                        : isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-50 hover:bg-gray-100'
                                        }`}
                                >
                                    <span className={`material-icons ${showStockPhotos ? 'text-white' : 'text-purple-400'}`}>
                                        collections
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm">Pexels Stock Photos</div>
                                        <div className={`text-xs ${showStockPhotos ? 'text-purple-200' : isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Search & download free photos
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    {showStockPhotos ? (
                        <StockPhotosBrowser
                            theme={theme}
                            language={language}
                            onSelect={onSelect}
                        />
                    ) : (
                        <div className="flex-1 flex flex-col overflow-hidden">
                            {/* Toolbar */}
                            <div className={`flex flex-wrap items-center gap-2 md:gap-4 p-3 md:p-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                                {/* Navigation */}
                                <div className="flex items-center gap-1 md:gap-2">
                                    <button
                                        onClick={goBack}
                                        disabled={pathHistory.length === 0}
                                        className={`p-1.5 md:p-2 rounded-lg disabled:opacity-30 ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                                    >
                                        <span className="material-icons text-lg md:text-xl">arrow_back</span>
                                    </button>
                                    <button
                                        onClick={goHome}
                                        className={`p-1.5 md:p-2 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                                    >
                                        <span className="material-icons text-lg md:text-xl">home</span>
                                    </button>
                                    <button
                                        onClick={browse}
                                        className={`p-1.5 md:p-2 rounded-lg ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                                    >
                                        <span className="material-icons text-lg md:text-xl">refresh</span>
                                    </button>
                                </div>

                                {/* Breadcrumb - hidden on mobile, shown on larger screens */}
                                <div className={`hidden md:flex flex-1 items-center gap-1 text-sm px-3 py-2 rounded-lg overflow-x-auto ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                                    <button onClick={goHome} className="hover:text-indigo-400 flex-shrink-0">
                                        {sources.find(s => s.id === activeSource)?.name || 'Root'}
                                    </button>
                                    {currentPath.split('/').filter(Boolean).map((segment, i, arr) => (
                                        <React.Fragment key={i}>
                                            <span className="text-slate-500">/</span>
                                            <span className={i === arr.length - 1 ? 'font-medium' : 'hover:text-indigo-400 cursor-pointer'}>
                                                {segment}
                                            </span>
                                        </React.Fragment>
                                    ))}
                                </div>

                                {/* Search */}
                                <div className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl flex-1 md:flex-none md:w-64 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                    <span className="material-icons text-slate-400 text-sm">search</span>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        placeholder={t.search}
                                        className={`w-full bg-transparent outline-none text-sm ${isDark ? 'placeholder:text-slate-500' : 'placeholder:text-gray-400'}`}
                                    />
                                </div>

                                {/* View Toggle */}
                                <div className={`flex rounded-xl overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-1.5 md:p-2 ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : ''}`}
                                    >
                                        <span className="material-icons text-sm">grid_view</span>
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`p-1.5 md:p-2 ${viewMode === 'list' ? 'bg-indigo-600 text-white' : ''}`}
                                    >
                                        <span className="material-icons text-sm">view_list</span>
                                    </button>
                                </div>
                            </div>

                            {/* File Grid/List */}
                            <div className="flex-1 overflow-y-auto p-4 min-h-0">
                                {isDisconnected ? (
                                    <div className="flex flex-col items-center justify-center h-full">
                                        <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${isDark ? 'bg-slate-800' : 'bg-white shadow-lg'}`}>
                                            <span className="material-icons text-5xl text-indigo-500">
                                                {currentSourceObj?.icon || 'cloud_off'}
                                            </span>
                                        </div>
                                        <h3 className="text-xl font-bold mb-2">Connect to {currentSourceObj?.name}</h3>
                                        <p className={`mb-8 max-w-md text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Access your {currentSourceObj?.name} files directly from the dashboard.
                                        </p>
                                        <a
                                            href={`/api/cloud-storage/auth/${activeSource?.replace('cloud-', '')}`}
                                            className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                                        >
                                            <span className="material-icons">login</span>
                                            Connect Account
                                        </a>
                                    </div>
                                ) : loading ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                            <div className="animate-spin w-10 h-10 border-3 border-indigo-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                                            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t.loading}</p>
                                        </div>
                                    </div>
                                ) : files.length === 0 ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                            <span className="material-icons text-6xl text-slate-600 mb-3">folder_off</span>
                                            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t.noFiles}</p>
                                        </div>
                                    </div>
                                ) : viewMode === 'grid' ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 pb-20">
                                        {files.map(file => {
                                            const isSelected = selectedFiles.some(f => f.id === file.id);
                                            return (
                                                <div
                                                    key={file.id}
                                                    onClick={() => {
                                                        if (onSelect) {
                                                            handleFileClick(file);
                                                        } else {
                                                            handlePreview(file);
                                                        }
                                                    }}
                                                    onContextMenu={(e) => handleContextMenu(e, file)}
                                                    className={`group relative aspect-square rounded-xl border transition-all cursor-pointer overflow-hidden ${isSelected
                                                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                                                        : isDark ? 'border-slate-700 bg-slate-800 hover:border-slate-600' : 'border-gray-200 bg-white hover:border-gray-300 shadow-sm hover:shadow'
                                                        }`}
                                                >
                                                    <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
                                                        {!file.isDirectory && isImage(file) && file.url ? (
                                                            <img
                                                                src={file.url}
                                                                alt={file.name}
                                                                className="w-full h-full object-cover"
                                                                loading="lazy"
                                                            />
                                                        ) : (
                                                            <span className={`material-icons text-4xl ${file.isDirectory ? 'text-amber-400' : 'text-slate-500'}`}>
                                                                {getFileIcon(file)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                                                        <p className="text-xs font-medium text-white truncate" title={file.name}>
                                                            {file.name}
                                                        </p>
                                                    </div>

                                                    {/* Quick Actions Overlay */}
                                                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        {!file.isDirectory && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                                                                className="p-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-lg"
                                                            >
                                                                <span className="material-icons text-xs">download</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {files.map(file => (
                                            <div
                                                key={file.path}
                                                onClick={() => handleFileClick(file)}
                                                onContextMenu={(e) => handleContextMenu(e, file)}
                                                className={`flex items-center gap-4 p-3 rounded-xl cursor-pointer transition-all ${selectedFiles.some(f => f.path === file.path)
                                                    ? 'bg-indigo-600/20 border-indigo-500'
                                                    : isDark ? 'hover:bg-slate-800 border-transparent' : 'hover:bg-gray-50 border-transparent'
                                                    } border`}
                                            >
                                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                                    {!file.isDirectory && isImage(file) && file.url ? (
                                                        <img src={file.url} alt="" className="w-full h-full object-cover rounded-lg" />
                                                    ) : (
                                                        <span className={`material-icons ${file.isDirectory ? 'text-amber-400' : 'text-slate-500'}`}>
                                                            {getFileIcon(file)}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium truncate">{file.name}</p>
                                                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                                        {file.isDirectory ? 'Folder' : formatSize(file.size)}
                                                        {file.modifiedAt && ` • ${new Date(file.modifiedAt).toLocaleDateString()}`}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {!file.isDirectory && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDelete(file); }}
                                                            className={`p-2 rounded-lg ${isDark ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-50 text-red-500'}`}
                                                        >
                                                            <span className="material-icons text-sm">delete</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Footer - Selected Info */}
                            {selectedFiles.length > 0 && (
                                <div className={`flex-shrink-0 px-4 py-3 border-t ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-white'}`}>
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm">
                                            {selectedFiles.length} file(s) selected
                                        </p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setSelectedFiles([])}
                                                className={`px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                                            >
                                                Clear
                                            </button>
                                            {onSelect && selectedFiles.length === 1 && (
                                                <button
                                                    onClick={() => onSelect(selectedFiles[0])}
                                                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium"
                                                >
                                                    {t.selectFile}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div >
                {/* Context Menu */}
                {
                    contextMenu.file && (
                        <div
                            className={`fixed z-[9999] rounded-xl shadow-2xl overflow-hidden min-w-[220px] py-2 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'}`}
                            style={{ top: Math.min(contextMenu.y, window.innerHeight - 350), left: Math.min(contextMenu.x, window.innerWidth - 240) }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={`px-4 py-2 text-xs font-bold border-b mb-2 uppercase tracking-wider ${isDark ? 'text-slate-500 border-slate-700' : 'text-gray-400 border-gray-100'}`}>
                                {contextMenu.file.name.length > 25 ? contextMenu.file.name.substring(0, 25) + '...' : contextMenu.file.name}
                            </div>

                            {/* Open / Preview */}
                            {!contextMenu.file.isDirectory && contextMenu.file.url && (
                                <button
                                    onClick={() => { handlePreview(contextMenu.file!); setContextMenu({ ...contextMenu, file: null }); }}
                                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
                                >
                                    <span className="material-icons text-sm text-cyan-400">visibility</span>
                                    {language === 'sv' ? 'Förhandsgranska' : 'Preview / Edit'}
                                </button>
                            )}

                            {/* Open in New Tab (Original Link) */}
                            {!contextMenu.file.isDirectory && contextMenu.file.url && (
                                <a
                                    href={contextMenu.file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
                                >
                                    <span className="material-icons text-sm text-gray-400">open_in_new</span>
                                    {language === 'sv' ? 'Öppna i ny flik' : 'Open in New Tab'}
                                </a>
                            )}

                            {/* Download */}
                            {!contextMenu.file.isDirectory && contextMenu.file.url && (
                                <button
                                    onClick={() => { handleDownload(contextMenu.file!); setContextMenu({ ...contextMenu, file: null }); }}
                                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
                                >
                                    <span className="material-icons text-sm text-blue-400">download</span>
                                    {language === 'sv' ? 'Ladda ner' : 'Download'}
                                </button>
                            )}

                            {/* Share */}
                            <button
                                onClick={() => { handleShare(contextMenu.file!); setContextMenu({ ...contextMenu, file: null }); }}
                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
                            >
                                <span className="material-icons text-sm text-green-400">share</span>
                                {language === 'sv' ? 'Dela' : 'Share'}
                            </button>

                            <div className={`my-2 border-t ${isDark ? 'border-slate-700' : 'border-gray-100'}`} />

                            {/* Rename */}
                            <button
                                onClick={() => { handleRename(contextMenu.file!); setContextMenu({ ...contextMenu, file: null }); }}
                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
                            >
                                <span className="material-icons text-sm text-indigo-400">edit</span>
                                {language === 'sv' ? 'Byt namn' : 'Rename'}
                            </button>

                            {/* Edit Image (only for images) */}
                            {!contextMenu.file.isDirectory && contextMenu.file.url &&
                                /\.(jpg|jpeg|png|gif|webp)$/i.test(contextMenu.file.name) && (
                                    <button
                                        onClick={() => { handleEditImage(contextMenu.file!); setContextMenu({ ...contextMenu, file: null }); }}
                                        className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
                                    >
                                        <span className="material-icons text-sm text-purple-400">tune</span>
                                        {language === 'sv' ? 'Redigera bild' : 'Edit Image'}
                                    </button>
                                )}

                            {/* Copy Link */}
                            <button
                                onClick={() => {
                                    if (contextMenu.file?.url) {
                                        navigator.clipboard.writeText(window.location.origin + contextMenu.file.url);
                                        showMessage('success', language === 'sv' ? 'Länk kopierad!' : 'Link copied!');
                                    }
                                    setContextMenu({ ...contextMenu, file: null });
                                }}
                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}
                            >
                                <span className="material-icons text-sm text-teal-400">link</span>
                                {language === 'sv' ? 'Kopiera länk' : 'Copy Link'}
                            </button>

                            <div className={`my-2 border-t ${isDark ? 'border-slate-700' : 'border-gray-100'}`} />

                            {/* Delete */}
                            <button
                                onClick={() => { handleDelete(contextMenu.file!); setContextMenu({ ...contextMenu, file: null }); }}
                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors hover:bg-red-500/10 text-red-500`}
                            >
                                <span className="material-icons text-sm">delete</span>
                                {t.delete}
                            </button>
                        </div>
                    )
                }

                {/* Image Editor Modal */}
                {
                    showImageEditor && editingFile && editingFile.url && (
                        <ImageEditor
                            imageUrl={editingFile.url}
                            imageName={editingFile.name}
                            onSave={handleSaveEditedImage}
                            onClose={() => { setShowImageEditor(false); setEditingFile(null); }}
                            theme={theme}
                            language={language}
                        />
                    )
                }

                {/* AI Image Generator Modal */}
                {
                    showAIGenerator && (
                        <AIImageGenerator
                            onGenerate={handleAIImageGenerated}
                            onClose={() => setShowAIGenerator(false)}
                            theme={theme}
                            language={language}
                        />
                    )
                }

                {/* File Viewer Modal */}
                {
                    showFileViewer && viewingFile && viewingFile.url && (
                        <FileViewer
                            fileUrl={viewingFile.url}
                            fileName={viewingFile.name}
                            onClose={() => { setShowFileViewer(false); setViewingFile(null); }}
                            onSave={handleSaveTextFile}
                            theme={theme}
                        />
                    )
                }
            </div >
        </div>
    );
};

export default UnifiedFileManager;
